import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { useOdontogramChart, useSaveOdontogramChart } from "@/queries/useOdontogramQuery";
import "./OdontogramPlugin.css";

const OdontogramChart = lazy(() => import("./OdontogramChart"));

// Display preferences of the doctor (no patient data): kept in the browser like
// the plugin list. The patient's dental chart itself is saved on the server per
// appointment.
export const ODONTOGRAM_SETTINGS_STORAGE_KEY = "doctolik-plugin-odontogram-settings";
export const ODONTOGRAM_LANGUAGES = [["fr", "Français"], ["en", "Anglais"], ["ar", "Arabe"], ["es", "Espagnol"], ["de", "Allemand"], ["it", "Italien"]];
export const ODONTOGRAM_NUMBERING = [["FDI", "FDI (ISO 3950)"], ["UNIVERSAL", "Universelle (US)"], ["PALMER", "Palmer"]];
export const DEFAULT_ODONTOGRAM_SETTINGS = { language: "fr", numberingSystem: "FDI", enableNotes: true, enableIcdas: false };
const SAVE_DELAY_MS = 800;
const oneOf = (options, value, fallback) => options.some(([id]) => id === value) ? value : fallback;

export function readOdontogramSettings() {
	try {
		const saved = JSON.parse(localStorage.getItem(ODONTOGRAM_SETTINGS_STORAGE_KEY)) || {};
		return {
			language: oneOf(ODONTOGRAM_LANGUAGES, saved.language, DEFAULT_ODONTOGRAM_SETTINGS.language),
			numberingSystem: oneOf(ODONTOGRAM_NUMBERING, saved.numberingSystem, DEFAULT_ODONTOGRAM_SETTINGS.numberingSystem),
			enableNotes: typeof saved.enableNotes === "boolean" ? saved.enableNotes : DEFAULT_ODONTOGRAM_SETTINGS.enableNotes,
			enableIcdas: saved.enableIcdas === true,
		};
	} catch {
		return DEFAULT_ODONTOGRAM_SETTINGS;
	}
}

function useOdontogramSettings() {
	const [settings, setSettings] = useState(readOdontogramSettings);
	useEffect(() => {
		const refresh = (event) => {
			if (!event.key || event.key === ODONTOGRAM_SETTINGS_STORAGE_KEY) setSettings(readOdontogramSettings());
		};
		window.addEventListener("storage", refresh);
		return () => window.removeEventListener("storage", refresh);
	}, []);
	return settings;
}

const Loading = () => <div className="OdontogramPluginMessage" role="status">Chargement de l'odontogramme…</div>;

export function OdontogramPluginSettings({ onClose }) {
	const [settings, setSettings] = useState(readOdontogramSettings);
	const update = (field, value) => setSettings((current) => ({ ...current, [field]: value }));
	const save = () => {
		localStorage.setItem(ODONTOGRAM_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
		// The storage event only reaches other tabs; notify this one too.
		window.dispatchEvent(new StorageEvent("storage", { key: ODONTOGRAM_SETTINGS_STORAGE_KEY }));
		onClose?.();
	};
	return <>
		<div className="PluginModalBody">
			<label>Langue de l'odontogramme
				<select value={settings.language} onChange={(event) => update("language", event.target.value)}>
					{ODONTOGRAM_LANGUAGES.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
				</select>
			</label>
			<label>Numérotation des dents
				<select value={settings.numberingSystem} onChange={(event) => update("numberingSystem", event.target.value)}>
					{ODONTOGRAM_NUMBERING.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
				</select>
			</label>
			<label><span>Notes par dent</span><input type="checkbox" checked={settings.enableNotes} onChange={(event) => update("enableNotes", event.target.checked)} /></label>
			<label><span>Score ICDAS des caries</span><input type="checkbox" checked={settings.enableIcdas} onChange={(event) => update("enableIcdas", event.target.checked)} /></label>
			<p className="OdontogramPluginHint">Aperçu avec une bouche vierge : aucune donnée patient n'est affichée ni enregistrée ici.</p>
			<div className="OdontogramPluginFrame odontogram-host">
				<Suspense fallback={<Loading />}>
					<OdontogramChart key={`${settings.language}-${settings.numberingSystem}-${settings.enableNotes}-${settings.enableIcdas}`} chart={null} settings={settings} />
				</Suspense>
			</div>
		</div>
		<div className="PluginModalFooter">
			<button className="Btn Btn--ghost" type="button" onClick={onClose}>Annuler</button>
			<button className="Btn" type="button" onClick={save}>Enregistrer</button>
		</div>
	</>;
}

const SAVE_LABELS = {
	idle: "",
	pending: "Modifications en attente…",
	saving: "Enregistrement…",
	saved: "Enregistré",
	error: "Échec de l'enregistrement",
};

export default function OdontogramPlugin({ appointmentId, readOnly = false }) {
	const settings = useOdontogramSettings();
	const { data, isLoading, isError, refetch } = useOdontogramChart(appointmentId);
	const { mutateAsync } = useSaveOdontogramChart(appointmentId);
	const [saveState, setSaveState] = useState("idle");
	const pending = useRef(null);
	const timer = useRef(null);
	// The chart handed to the engine is fixed for this appointment view, so a
	// save echoing back through the query cache does not re-import it mid-edit.
	const [initialChart, setInitialChart] = useState(undefined);
	// Latest edited chart, handed to the engine if it remounts (e.g. a settings change).
	const [editedChart, setEditedChart] = useState(null);
	useEffect(() => {
		if (data && initialChart === undefined) setInitialChart(data.chart ?? null);
	}, [data, initialChart]);

	const flush = useCallback(async () => {
		clearTimeout(timer.current);
		timer.current = null;
		const chart = pending.current;
		if (!chart) return;
		pending.current = null;
		setSaveState("saving");
		try {
			await mutateAsync(chart);
			setSaveState(pending.current ? "pending" : "saved");
		} catch {
			pending.current ??= chart;
			setSaveState("error");
		}
	}, [mutateAsync]);

	const handleChange = useCallback((chart) => {
		if (readOnly) return;
		setEditedChart(chart);
		pending.current = chart;
		setSaveState("pending");
		clearTimeout(timer.current);
		timer.current = setTimeout(flush, SAVE_DELAY_MS);
	}, [flush, readOnly]);

	// Do not lose the last edit when the doctor switches plugin or leaves the consultation.
	useEffect(() => () => {
		if (timer.current) flush();
	}, [flush]);

	if (!appointmentId) return <div className="OdontogramPluginMessage">Ouvrez une consultation pour afficher l'odontogramme du patient.</div>;
	if (isLoading || (data && initialChart === undefined)) return <Loading />;
	if (isError) return <div className="OdontogramPluginMessage" role="alert">Impossible de charger l'odontogramme.<button type="button" onClick={() => refetch()}>Réessayer</button></div>;

	return <div className="OdontogramPlugin">
		<div className="OdontogramPluginBar">
			<span>{data?.source === "previous" ? "Repris de la dernière consultation de ce patient" : data?.source === "appointment" ? "Odontogramme de cette consultation" : "Nouvel odontogramme"}{readOnly ? " · lecture seule" : ""}</span>
			<span className={`OdontogramPluginSave is-${saveState}`} role="status">
				{SAVE_LABELS[saveState]}
				{saveState === "error" && <button type="button" onClick={flush}>Réessayer</button>}
			</span>
		</div>
		<div className="OdontogramPluginFrame odontogram-host">
			<Suspense fallback={<Loading />}>
				<OdontogramChart key={`${appointmentId}-${settings.language}-${settings.numberingSystem}-${settings.enableNotes}-${settings.enableIcdas}`} chart={editedChart ?? initialChart} settings={settings} readOnly={readOnly} onChange={handleChange} />
			</Suspense>
		</div>
	</div>;
}
